import React, { useMemo } from "react";
import Highcharts from "highcharts";
import { renderToString } from "react-dom/server";
import { useTranslation } from "react-i18next";
import { PerformanceValuationTooltip } from "@components/Tooltip/PerformanceValuationTooltip";
import { GainLossChartPercents } from "@components/Tooltip/GainLossChartPercents";
import {
  toAmericanShort,
  toCurrency,
  useDateFormatterForXAxis,
} from "@helpers";
import { InvestmentChartDataItem, usePerformanceTrendColor } from "@hooks";
import { Theme } from "@mui/material";
import { getCommonHighchartsOptions } from "../commonChartOptions";

interface Props {
  chartData: {
    totalValues: InvestmentChartDataItem[];
    investedCapitalValues: InvestmentChartDataItem[];
    comparisonValues: InvestmentChartDataItem[];
  };
  theme: Theme;
  withInvestedCapital: boolean;
  seriesNameMap?: {
    totalValues: string;
    investedCapitalValues?: string;
    comparisonValues?: string;
  };
  colors?: {
    totalValues: string;
    investedCapitalValues?: string;
    comparisonValues?: string;
  };
  tooltipType?: "value" | "percentage";
  legendCircleSize?: number;
  height?: number;
  labelStyle?: {
    fontSize?: string;
    lineHeight?: string;
    fontWeight?: string;
    textTransform?: "none" | "uppercase";
  };
}

export const useHighchartsOptions = ({
  chartData,
  theme,
  withInvestedCapital,
  seriesNameMap,
  colors,
  tooltipType,
  legendCircleSize,
  labelStyle,
  height,
}: Props): Highcharts.Options => {
  const { t } = useTranslation();
  const commonHighchartsOptions = useMemo(
    () => getCommonHighchartsOptions(theme),
    [theme]
  );

  const startValue = chartData.totalValues[0]?.[1] ?? 0;
  const endValue =
    chartData.totalValues[chartData.totalValues.length - 1]?.[1] ?? 0;
  const trendColorFromHook = usePerformanceTrendColor(startValue, endValue);
  const trendColor = colors?.totalValues ?? trendColorFromHook;
  const xAxisFormatter = useDateFormatterForXAxis();

  return useMemo(() => {
    return {
      credits: {
        enabled: false,
      },
      chart: {
        type: "line",
        ...commonHighchartsOptions.chart,
        ...(height ? { height } : {}),
      },
      title: {
        text: "",
      },
      legend: {
        ...commonHighchartsOptions.legend,
        ...(legendCircleSize && {
          symbolHeight: legendCircleSize,
          symbolWidth: legendCircleSize,
        }),
      },
      tooltip: {
        ...commonHighchartsOptions.tooltip,
        padding: 0,
        split: true,
        formatter: function () {
          return PerformanceValuationTooltip({
            formatter: (value) =>
              tooltipType === "percentage" ? (
                <GainLossChartPercents
                  value={(value as number) / 100}
                  theme={theme}
                />
              ) : (
                toCurrency(value as number, "EUR")
              ),
            points: this.points,
            theme,
            x: this.x,
          });
        },
      },
      xAxis: {
        type: "datetime",
        tickLength: 0,
        lineColor: theme.palette.border.secondary,
        crosshair: {
          color: theme.palette.border.secondary,
        },
        labels: {
          style: {
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.caption.fontSize,
            color: theme.palette.text.secondary,
            fontWeight: "400",
            ...labelStyle,
          },
          formatter: function (
            data: Highcharts.AxisLabelsFormatterContextObject
          ) {
            const selectedDates = this.chart.xAxis[0].getExtremes();
            const startDate = new Date(selectedDates.min);
            const endDate = new Date(selectedDates.max);
            return xAxisFormatter(
              startDate,
              endDate,
              data.value,
              (this.axis as unknown as { tickInterval: number }).tickInterval
            );
          },
        },
      },
      yAxis: {
        title: {
          text: "",
        },
        tickAmount: 6,
        gridLineColor: theme.palette.border.secondary,
        labels: {
          formatter: (data) => {
            return renderToString(
              <div
                style={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: theme.typography.caption.fontSize,
                  color: theme.palette.text.secondary,
                  fontWeight: 400,
                  ...labelStyle,
                }}
              >
                {toAmericanShort(data.value as number, 0, 2)}
              </div>
            );
          },
        },
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false,
            symbol: "circle",
            states: {
              hover: {
                radius: 4,
                lineWidth: 1,
                lineWidthPlus: 0,
              },
            },
          },
        },
        line: {
          legendSymbol: "rectangle",
          lineWidth: 2,
        },
        area: {
          threshold: null,
          fillColor: {
            stops: [
              [0, Highcharts.color(trendColor).setOpacity(0.1).get("rgba")],
              [1, Highcharts.color(trendColor).setOpacity(0).get("rgba")],
            ],
            linearGradient: {
              x1: 0,
              y1: 0,
              x2: 0,
              y2: 1,
            },
          },
          legendSymbol: "rectangle",
          lineWidth: 2,
        },
      },
      series: [
        {
          name:
            seriesNameMap?.totalValues ??
            t("dashboardPage.dashboard.performance.tabs.value.portfolio"),
          data: chartData.totalValues,
          type: "area",
          color: trendColor,
        },
        ...(chartData.comparisonValues.length > 0 &&
        seriesNameMap?.comparisonValues
          ? [
              {
                name: seriesNameMap.comparisonValues,
                data: chartData.comparisonValues,
                type: "area",
                color:
                  colors?.comparisonValues ??
                  theme.palette.graphVisual["03"].light,
              },
            ]
          : []),

        ...(withInvestedCapital
          ? [
              {
                name:
                  seriesNameMap?.investedCapitalValues ||
                  t(
                    "dashboardPage.dashboard.performance.tabs.value.investedCapitol"
                  ),
                data: chartData.investedCapitalValues,
                color:
                  colors?.investedCapitalValues ??
                  theme.palette.graphVisual["01"].light,
                type: "line",
              } as Highcharts.SeriesOptionsType,
            ]
          : []),
      ],
    } as Highcharts.Options;
  }, [
    height,
    chartData,
    theme,
    withInvestedCapital,
    commonHighchartsOptions,
    seriesNameMap,
    colors,
    trendColor,
    t,
    tooltipType,
    legendCircleSize,
    labelStyle,
    xAxisFormatter,
  ]);
};
